import { closedFencedBlocks } from './factory-progress';
import { parseGithubPrUrl } from './github-pr';

/**
 * SF-T-173: the coding agent's word on why it opened no pull request on the run's branch. A run that
 * finds no PR there fails, unless the agent ended its turn with one of these (or the task already has
 * an open PR linked):
 *
 * ```factory-pr
 * { "pr": "https://github.com/owner/repo/pull/49", "reason": "The change is all in owner/repo." }
 * ```
 *
 * `pr` is the pull request the work went in instead (another repo, or one already open on the task), or
 * null when the task needed none; `reason` says why, and is required when `pr` is null. The run's
 * `task.pr_decision` step reads it.
 */

export const FACTORY_PR_LANG = 'factory-pr';

const REASON_MAX = 600;

export interface FactoryPrDecision {
  /** A github.com pull request address, or null for none. */
  pr: string | null;
  /** Why there's no PR on the branch ('' when the agent didn't say, only allowed with a `pr`). */
  reason: string;
}

/** A block's fields, checked; null when `pr` isn't a GitHub pull request address, or there's neither a PR nor a reason. */
export function cleanFactoryPr(data: unknown): FactoryPrDecision | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const obj = data as Record<string, unknown>;
  const reason = typeof obj.reason === 'string' ? obj.reason.replace(/\s+/g, ' ').trim().slice(0, REASON_MAX) : '';
  const raw = typeof obj.pr === 'string' ? obj.pr.trim() : obj.pr ?? null;
  if (raw === null || raw === '') return reason ? { pr: null, reason } : null;
  if (typeof raw !== 'string' || !parseGithubPrUrl(raw)) return null;
  return { pr: raw, reason };
}

/** The last usable factory-pr block in the message (a later one corrects an earlier one), or null. */
export function findFactoryPr(message: string): FactoryPrDecision | null {
  const blocks = closedFencedBlocks(message, FACTORY_PR_LANG);
  for (let i = blocks.length - 1; i >= 0; i--) {
    try {
      const found = cleanFactoryPr(JSON.parse(blocks[i]!));
      if (found) return found;
    } catch {
      // Not JSON: look at the one before.
    }
  }
  return null;
}
